import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/layout/AppShell";
import { Button, Input, Label } from "../components/common/UIKit";
import CheckboxList from "../components/common/CheckboxList";
import ChipInput from "../components/common/ChipInput";
import DatePicker from "../components/common/DatePicker";
import PresetBar from "../components/common/PresetBar";
import MapDrawingFilter from "../components/common/MapDrawingFilter";
import { scoreValueToLevel } from "../components/common/ConfidenceScorePicker";
import { useNavigate, useLocation } from "react-router-dom";
import { getAccess, claimPendingSubscriptions } from "../api/subscriptions";
import { LEAD_TYPES_META } from "../constants/leadTypes";
import { Filter, Search, Info, RotateCcw, MapPin, X ,ArrowRight} from "lucide-react";




const emptyFilters = {
  start_date: "", end_date: "", date_range: "",
  price_min: "", price_max: "",
  states: [], counties: [], cities: [], zips: [],
  lead_types: [], property_type: [],
  owner_occupied: "", confidence_level: "",
  has_phone_number: "", show_blacklisted: "false", show_address_match: "",
  remove_dnc: "false", has_notes: "",
  map_polygon: "",
};

const DATE_RANGE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
];

function FilterSection({ id, title, children }) {
  return (
    <div className="border-b border-zinc-200">
      <div className="w-full px-5 py-3 text-sm font-bold tracking-tight text-zinc-950"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        data-testid={id ? `filter-section-${id}` : undefined}>
        {title}
      </div>
      <div className="px-5 pb-4">{children}</div>
    </div>
  );
}

function PriceInput({ testId, value, onChange, placeholder }) {
  return (
    <div className="relative">
      <Input data-testid={testId} type="number" value={value}
        onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="pr-7" />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400 pointer-events-none">$</span>
    </div>
  );
}

export default function LeadsPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Restore filters if coming back from results page.
  // Deep-merge with emptyFilters so all array fields always exist (prevents
  // `.filter()` crash when location.state has no filters key at all).
  const [pending, setPending] = useState({ ...emptyFilters, ...(location.state?.filters || {}) });
  const [access, setAccess] = useState([]);
  const [accessLoaded, setAccessLoaded] = useState(false);
  useEffect(() => {
    claimPendingSubscriptions().catch(() => {});
    getAccess().then(setAccess).catch(() => {}).finally(() => setAccessLoaded(true));
  }, []);

  const setField = (k, v) => setPending((f) => ({ ...f, [k]: v }));

  const reset = () => setPending(emptyFilters);

  const applyPreset = (presetFilters) => {
    const merged = { ...emptyFilters, ...presetFilters };
    if (merged.confidence_score_min && !merged.confidence_level) {
      merged.confidence_level = scoreValueToLevel(merged.confidence_score_min) || "";
    }
    setPending(merged);
    navigate("/leads/results", { state: { filters: merged, fromFilters: true } });
  };

  const search = () => {
    navigate("/leads/results", { state: { filters: pending, fromFilters: true } });
  };

  const myStates = useMemo(() => Array.from(new Set(access.map((a) => a.state).filter(Boolean))).sort(), [access]);
  const myCounties = useMemo(() => Array.from(new Set(access.map((a) => a.county_name).filter(Boolean))).sort(), [access]);

  const accessByLeadType = useMemo(() => {
    const byType = new Map();
    access.forEach((item) => {
      const county = item.county_name;
      if (!county) return;
      const leadTypes = item.is_all_types
        ? LEAD_TYPES_META.map((lt) => lt.name)
        : (item.lead_types || []);
      leadTypes.forEach((type) => {
        if (!byType.has(type)) byType.set(type, new Set());
        byType.get(type).add(county);
      });
    });
    return byType;
  }, [access]);

  const countyOptions = useMemo(() => {
    const selectedStates = new Set((pending.states || []).map((s) => String(s).trim().toLowerCase()));
    const scopedCounties = access
      .filter((a) => !selectedStates.size || selectedStates.has(String(a.state || "").trim().toLowerCase()))
      .map((a) => a.county_name).filter(Boolean);
    const uniqueCounties = Array.from(new Set(scopedCounties.length ? scopedCounties : myCounties)).sort();
    return uniqueCounties.map((c) => ({ value: c, label: c.toUpperCase() }));
  }, [access, myCounties, pending.states]);

  const subscribedLeadTypes = useMemo(() => {
    if (!accessLoaded || !access.length) return [];
    return Array.from(accessByLeadType.keys());
  }, [accessLoaded, access, accessByLeadType]);

  const availableLeadTypes = useMemo(() => {
    const countyValues = new Set(countyOptions.map((c) => c.value));
    const selectedCounties = (pending.counties || []).filter((county) => countyValues.has(county));
    // No counties selected = no filter applied, show all subscribed types
    if (selectedCounties.length === 0) return subscribedLeadTypes;
    // Some or all counties selected — only show types that cover every selected county
    return subscribedLeadTypes.filter((type) => {
      const coveredCounties = accessByLeadType.get(type) || new Set();
      return selectedCounties.every((county) => coveredCounties.has(county));
    });
  }, [accessByLeadType, subscribedLeadTypes, countyOptions, pending.counties]);

  const statusOptions = useMemo(
    () => LEAD_TYPES_META.filter((lt) => availableLeadTypes.includes(lt.name)).map((lt) => ({ value: lt.name, label: lt.label })),
    [availableLeadTypes]
  );

  useEffect(() => {
    setPending((prev) => {
      const next = prev.lead_types.filter((s) => availableLeadTypes.includes(s));
      return next.length === prev.lead_types.length ? prev : { ...prev, lead_types: next };
    });
  }, [availableLeadTypes]);

  useEffect(() => {
    const allowed = new Set(countyOptions.map((c) => c.value));
    setPending((prev) => {
      const next = prev.counties.filter((c) => allowed.has(c));
      return next.length === prev.counties.length ? prev : { ...prev, counties: next };
    });
  }, [countyOptions]);

  return (
    <AppShell>
      <div className="flex flex-col h-[100vh] md:h-[100vh]">
        <PresetBar mode="list" currentFilters={pending} onApply={applyPreset} />
        <div className="flex flex-col md:flex-row flex-1 min-h-0">
          {/* Filters sidebar — full-width on mobile, side panel on md+ */}
          <aside className="w-full md:w-[34rem] md:flex-none border-b md:border-b-0 md:border-r border-zinc-200 bg-zinc-50/40 flex flex-col" data-testid="leads-filter-panel">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-700 bg-white sticky top-0 z-10">
              <Filter className="h-3.5 w-3.5" /> Filters
            </div>
            <div className="flex-1 overflow-y-auto">

              <FilterSection id="date-price" title="Date and Price">
                <div className="space-y-3">
                  <div>
                    <Label className="mb-1.5">Quick Date Range</Label>
                    <div className="flex flex-wrap gap-1.5" data-testid="filter-date-range-presets">
                      {DATE_RANGE_PRESETS.map(({ value, label }) => {
                        const active = pending.date_range === value;
                        return (
                          <button key={value} type="button" data-testid={`filter-date-range-${value}`}
                            onClick={() => setPending((f) => ({ ...f, date_range: active ? "" : value, start_date: active ? f.start_date : "", end_date: active ? f.end_date : "" }))}
                            className={`h-7 px-2.5 rounded-md text-xs font-semibold border transition-colors ${active ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-950"}`}>
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="w-24 mb-0">Custom</Label>
                    <div className="flex-1 flex items-center gap-1.5">
                      <DatePicker testId="filter-start-date" value={pending.start_date}
                        onChange={(v) => setPending((f) => ({ ...f, start_date: v, date_range: v ? "" : f.date_range }))} placeholder="Start Date" />
                      <span className="text-zinc-400">—</span>
                      <DatePicker testId="filter-end-date" value={pending.end_date}
                        onChange={(v) => setPending((f) => ({ ...f, end_date: v, date_range: v ? "" : f.date_range }))} placeholder="End Date" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="w-24 mb-0">Price Range</Label>
                    <div className="flex-1 flex items-center gap-1.5">
                      <PriceInput testId="filter-min-price" value={pending.price_min} onChange={(v) => setField("price_min", v)} placeholder="From" />
                      <span className="text-zinc-400">—</span>
                      <PriceInput testId="filter-max-price" value={pending.price_max} onChange={(v) => setField("price_max", v)} placeholder="To" />
                    </div>
                  </div>
                </div>
              </FilterSection>

              <FilterSection id="state-county" title="State & County">
                <div className="space-y-2">
                  {myStates.length === 0 ? (
                    <p className="text-sm text-zinc-500 py-2">No states available</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {myStates.map((state) => <Label key={state}>{state}</Label>)}
                    </div>
                  )}
                  <CheckboxList testId="filter-county" values={pending.counties}
                    onChange={(v) => setField("counties", v)} options={countyOptions}
                    searchPlaceholder="Search County" showSelectAll={false} />
                </div>
              </FilterSection>

              <FilterSection id="city-zip" title="City and Zip">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Cities</Label>
                    <ChipInput testId="filter-city" values={pending.cities} onChange={(v) => setField("cities", v)} placeholder="Add city + Enter" />
                  </div>
                  <div>
                    <Label>ZIPs</Label>
                    <ChipInput testId="filter-zip" values={pending.zips} onChange={(v) => setField("zips", v)} placeholder="Add ZIP + Enter" />
                  </div>
                </div>
              </FilterSection>

              <FilterSection id="status" title="Status">
                <CheckboxList testId="filter-status" values={pending.lead_types}
                  onChange={(v) => setField("lead_types", v)} options={statusOptions}
                  showSearch={false} showSelectAll={false} />
                {statusOptions.length === 0 && (
                  <p className="text-xs text-zinc-500 mt-2" data-testid="filter-status-empty">
                    No lead types available for the selected counties
                  </p>
                )}
              </FilterSection>

              <FilterSection id="additional" title="Additional">
                <div className="space-y-2">
                  {[{ k: "has_phone_number", label: "Includes a valid phone", tooltip: "Only return leads with phone contact info." }].map(({ k, label, tooltip }) => (
                    <label key={k} className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer" data-testid={`filter-${k}-wrap`}>
                      <input type="checkbox" data-testid={`filter-${k}`} checked={pending[k] === "true"}
                        onChange={(e) => setField(k, e.target.checked ? "true" : (k === "show_blacklisted" || k === "remove_dnc") ? "false" : "")}
                        className="h-4 w-4 rounded border-zinc-300 text-red-600 accent-red-600" />
                      {label}
                      <Info className="h-3.5 w-3.5 text-zinc-400" title={tooltip} />
                    </label>
                  ))}
                </div>
              </FilterSection>

              {/* Map polygon indicator */}
              {pending.map_polygon && (
                <div className="px-5 py-3 border-b border-zinc-200">
                  <div className="flex items-center justify-between gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="font-semibold">Map area filter active</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setField("map_polygon", "")}
                      className="h-4 w-4 flex items-center justify-center rounded hover:bg-blue-200 text-blue-500 hover:text-blue-800 flex-shrink-0"
                      title="Remove map filter"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              )}
            </div>


        <div
          className="border-t border-zinc-200 bg-white p-3 sticky bottom-0 flex items-center gap-2"
          data-testid="filter-actions"
        >
          <Button
            variant="outline"
            data-testid="reset-filters-button"
            onClick={reset}
            disabled={access.length === 0}
            className="h-10 px-3"
          >
            <RotateCcw className="h-4 w-4 mr-1" /> Reset
          </Button>

          <Button
            variant="accent"
            data-testid="apply-filters-button"
            onClick={search}
            disabled={access.length === 0}
            className="h-10 flex-1"
          >
            <Search className="h-4 w-4 mr-1" /> Search
          </Button>
        </div>


          </aside>

          {/* Map panel — hidden on mobile per requirement */}
        <div className="hidden md:flex flex-1 min-w-0 min-h-0 flex-col">
       {!accessLoaded ? null : access.length > 0 ? (
          <>
            <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 text-xs text-zinc-500 flex-shrink-0">
              New to map search? View a quick{" "}
              <button
                type="button"
                onClick={() => navigate("/leads/map-help")}
                className="text-blue-600 hover:text-blue-800 font-semibold underline underline-offset-2"
              >
                drawing tool tutorial
              </button>
              .
            </div>

            <div className="flex-1 min-h-0 p-4">
              {accessLoaded && (
                <div className="h-full w-full rounded-xl border border-zinc-200 overflow-hidden">
                  <MapDrawingFilter
                    access={access}
                    onPolygonChange={(geoJson) =>
                      setField("map_polygon", geoJson)
                    }
                  />
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center">
              {/* <h3 className="text-lg font-semibold text-zinc-900">
                No active subscriptions
              </h3> */}

              <p className="mt-2 text-sm text-zinc-500">
                {/* Please subscribe to access leads. */}
                No active subscriptions
              </p>

             <button
            type="button"
            onClick={() => navigate("/subscriptions")}
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg bg-black px-4 py-2 text-sm font-medium text-white hover:bg-zinc-800"
          >
            Purchase Subscriptions
            <ArrowRight className="h-4 w-4" />
          </button>
            </div>
          </div>
        )}
      </div>
        </div>
      </div>
    </AppShell>
  );
}
